import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useModal } from 'components/modals/ModalContext';
import { saveData } from 'services/api/saveData';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import NewsletterSendNotice from './NewsletterSendNotice';
import { AudiencePreviewRecipient, NewsletterSendStatus } from './NewsletterTypes';

interface NewsletterSendModalProps {
    newsletterId: number;
    subject: string;
    audience: number | null; // The chosen audience, every contact with an email address without one
    sendStatus: NewsletterSendStatus | null;
    mode?: 'preview' | 'send'; // The preview mode only shows the newsletter and the recipients, without the send button
    onSent: () => void;
}

// The send modal of a newsletter: the preview in its layout, the recipients and the test and send buttons
const NewsletterSendModal: React.FC<NewsletterSendModalProps> = ({ newsletterId, subject, audience, sendStatus, mode = 'send', onSent }) => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const { revealModal, closeModal, closeAllModals } = useModal();
    const shadowRootRef = useRef<HTMLDivElement>(null);
    const [html, setHtml] = useState<string | null>(null);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [recipients, setRecipients] = useState<AudiencePreviewRecipient[]>([]);
    const [recipientCount, setRecipientCount] = useState<number | null>(null);
    const [recipientPage, setRecipientPage] = useState(1);
    const [recipientPageSize, setRecipientPageSize] = useState(50);
    const [loadingRecipients, setLoadingRecipients] = useState(false);
    const [testLoading, setTestLoading] = useState(false);
    const [sendLoading, setSendLoading] = useState(false);
    const [confirming, setConfirming] = useState(false);

    // Reveal the modal directly, the preview and recipients load inside it
    useEffect(() => {
        revealModal();
    }, []);

    // Load the rendered preview of the newsletter
    useEffect(() => {
        const loadPreview = async () => {
            try {
                const response = await saveData({ apiUrl: `post_newsletter_preview/${newsletterId}`, method: 'post', data: {} });
                setHtml(response?.data?.html ?? '');
            } catch (error) {
                setPreviewError('newsletters.send.preview_failed');
            }
        };
        loadPreview();
    }, [newsletterId]);

    // Load a page of the resolved recipients
    const loadRecipients = async (page: number) => {
        setLoadingRecipients(true);
        try {
            const requestData = audience ? { audience, page } : { audience_conditions: [], page };
            const response = await saveData({ apiUrl: 'post_newsletter_audience_preview', method: 'post', data: requestData });
            const data = response?.data;
            setRecipientCount(data?.count ?? 0);
            setRecipientPageSize(data?.page_size ?? 50);
            setRecipientPage(page);
            setRecipients(current => (page === 1 ? data?.results ?? [] : [...current, ...(data?.results ?? [])]));
        } catch (error) {
            setRecipientCount(0);
        } finally {
            setLoadingRecipients(false);
        }
    };

    // Load the first page of recipients on mount
    useEffect(() => {
        loadRecipients(1);
    }, [newsletterId]);

    // Put the rendered html in the shadow root so the email styles stay isolated
    useEffect(() => {
        if (!shadowRootRef.current || html === null) return;
        const shadowRoot = shadowRootRef.current.shadowRoot ?? shadowRootRef.current.attachShadow({ mode: 'open' });
        shadowRoot.innerHTML = html;
    }, [html]);

    // Sends the test email to the request user
    const handleTestSend = async () => {
        setTestLoading(true);
        try {
            const response = await saveData({ apiUrl: `post_newsletter_test/${newsletterId}`, method: 'post', data: {} });
            setFloatingAlert({ type: 'success', message: t('newsletters.send.test_sent', { email: response?.data?.sent_to ?? '' }) });
        } catch (error: any) {
            setFloatingAlert({ type: 'danger', message: error?.response?.data?.general ?? 'newsletters.send.test_failed' });
        } finally {
            setTestLoading(false);
        }
    };

    // Sends the newsletter to the audience after the confirmation
    const handleSend = async () => {
        setSendLoading(true);
        try {
            await saveData({ apiUrl: `post_newsletter_send/${newsletterId}`, method: 'post', data: {} });
            setFloatingAlert({ type: 'success', message: 'newsletters.send.sent_message' });
            closeAllModals();
            onSent();
        } catch (error: any) {
            setFloatingAlert({ type: 'danger', message: error?.response?.data?.general ?? 'newsletters.send.send_failed' });
            setSendLoading(false);
            setConfirming(false);
        }
    };

    // Whether more recipients can be loaded
    const hasMoreRecipients = recipientCount !== null && recipients.length < recipientCount;

    // Sending is blocked without an own verified domain
    const sendBlocked = Boolean(sendStatus && !sendStatus.can_send);
    const blockedMessage = sendBlocked ? t('newsletters.status.custom_domain_required') : undefined;

    return (
        <div className='email-preview-modal nl-send-modal'>
            <div className='nl-send-columns'>
                <div className='nl-send-preview'>
                    <div className='nl-send-column-title'>{t('newsletters.send.preview_title')}</div>
                    {previewError && <div className='kb-field-error'>{t(previewError)}</div>}
                    {html === null && !previewError && <div className='nl-send-loading'>{t('newsletters.send.loading_preview')}</div>}
                    <div className='nl-send-preview-frame' ref={shadowRootRef} />
                </div>
                <div className='nl-send-recipients'>
                    <div className='nl-send-column-title'>{t('newsletters.send.recipients_title')}</div>
                    <NewsletterSendNotice status={sendStatus} compact={true} />
                    <div className='nl-send-summary'>
                        <div className='nl-send-summary-row'>
                            <span className='nl-send-summary-label'>{t('newsletters.send.subject_label')}</span>
                            <span className='nl-send-summary-value'>{subject || t('newsletters.general.untitled')}</span>
                        </div>
                        <div className='nl-send-summary-row'>
                            <span className='nl-send-summary-label'>{t('newsletters.send.recipient_count_label')}</span>
                            <span className='nl-send-summary-value'>{recipientCount === null ? '…' : t('newsletters.send.recipient_count', { count: recipientCount })}</span>
                        </div>
                    </div>
                    <div className='nl-send-recipient-list'>
                        {recipients.map(recipient => (
                            <div key={`${recipient.contact_id}-${recipient.email}`} className='nl-send-recipient'>
                                <span className='nl-send-recipient-name'>{recipient.name}</span>
                                <span className='nl-send-recipient-email'>{recipient.email}</span>
                            </div>
                        ))}
                        {recipientCount === 0 && <div className='nl-send-loading'>{t('newsletters.send.no_recipients')}</div>}
                        {hasMoreRecipients && (
                            <button type='button' className='nl-send-load-more' disabled={loadingRecipients} onClick={() => loadRecipients(recipientPage + 1)}>
                                {t('newsletters.send.load_more', { shown: recipients.length, total: recipientCount, size: recipientPageSize })}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            <div className='footer'>
                <div className='left-content'>
                    {confirming && (
                        <span className='nl-send-confirm-text'>{t('newsletters.send.confirm_text', { count: recipientCount ?? 0 })}</span>
                    )}
                </div>
                <div className='right-content'>
                    {confirming ? (
                        <>
                            <SecondaryButton onClick={() => setConfirming(false)} label='general.cancel' size='small' />
                            <PrimaryButton onClick={handleSend} label='newsletters.send.confirm_button' size='small' loading={sendLoading} />
                        </>
                    ) : (
                        <>
                            <SecondaryButton onClick={closeModal} label='general.close' size='small' />
                            <SecondaryButton
                                onClick={handleTestSend}
                                label='newsletters.send.test_button'
                                size='small'
                                loading={testLoading}
                                disabled={{ disabled: sendBlocked, message: blockedMessage }} />
                            {mode === 'send' && (
                                <PrimaryButton
                                    onClick={() => setConfirming(true)}
                                    label='newsletters.send.send_button'
                                    size='small'
                                    disabled={{ disabled: sendBlocked || !recipientCount || html === null, message: blockedMessage ?? (!recipientCount ? t('newsletters.send.no_recipients') : undefined) }} />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewsletterSendModal;
